import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, IconButton, Fade, Button, CircularProgress, Drawer, Divider } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isAndroid, isIOS, mobile, isMobileWebBrowser } from "src/utils/platformUtils";
import AiSwitchButton from "./AiSwitchButton";
import { WidgetContext } from "src/contexts/WidgetContext";

// mock up for ui settings dialog
const aiSettingsMockOptions = [
  // {
  //   id: "audio",
  //   title: "Audio AI Responses",
  //   description: "Enable or disable audio generation for premium sections.",
  // },
  {
    id: "quan",
    title: "Quan",
    description: "Enable or disable video character Quan in the app",
  },
  // {
  //   id: "example3",
  //   title: "Title 3",
  //   description: "The text that we want to describe this",
  // },
];

export interface AiSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (nextSettings: Record<string, boolean>) => Promise<void> | void;
  isLoading: boolean;
  enabledSettings: Record<string, boolean>;
}

export const AiSettingsDialog: React.FC<AiSettingsDialogProps> = ({ open, onClose, onConfirm, isLoading, enabledSettings }) => {
  const { aiSettings } = useContext(WidgetContext);
  // Get the option ids from the mock options
  const optionIds = useMemo(() => aiSettingsMockOptions.map((option) => option.id), []);
  // Function to normalize the settings to the option ids
  const normalizeSettings = useCallback(
    (settings: Record<string, boolean>) =>
      optionIds.reduce<Record<string, boolean>>((acc, id) => {
        acc[id] = settings[id] ?? false;
        return acc;
      }, {}),
    [optionIds]
  );

  // Initialize the local settings with the normalized enabled settings
  const [localSettings, setLocalSettings] = useState<Record<string, boolean>>(() => normalizeSettings(enabledSettings));
  // Initialize the initial settings reference with the normalized enabled settings
  const initialSettingsRef = useRef<Record<string, boolean>>(normalizeSettings(enabledSettings));
  // Initialize the show unsaved prompt state
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);

  const isMobile = mobile || isMobileWebBrowser();
  // Use effect to initialize the local settings and show unsaved prompt when the dialog is opened
  useEffect(() => {
    if (open) {
      const normalized = normalizeSettings(enabledSettings);
      initialSettingsRef.current = normalized;
      setLocalSettings(normalized);
      setShowUnsavedPrompt(false);
    }
  }, [open, enabledSettings, normalizeSettings]);

  // Check if there are changes between the local settings and the initial settings
  const hasChanges = useMemo(() => JSON.stringify(localSettings) !== JSON.stringify(initialSettingsRef.current), [localSettings]);

  // Function to toggle the local setting
  const handleToggleLocalSetting = (settingId: string, nextValue: boolean) => {
    setLocalSettings((prev) => ({
      ...prev,
      [settingId]: nextValue,
    }));
  };

  // Function to handle the submit of the form
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isLoading || !hasChanges) {
      return;
    }
    await onConfirm(localSettings);
    initialSettingsRef.current = normalizeSettings(localSettings);
  };

  // Function to handle the close attempt of the dialog
  const handleCloseAttempt = () => {
    if (isLoading) {
      return;
    }
    if (hasChanges) {
      setShowUnsavedPrompt(true);
      return;
    }
    onClose();
  };

  // Function to handle the discard changes of the dialog
  const handleDiscardChanges = () => {
    setShowUnsavedPrompt(false);
    setLocalSettings({ ...initialSettingsRef.current });
    onClose();
  };

  // Function to handle the keep editing of the dialog
  const handleKeepEditing = () => {
    setShowUnsavedPrompt(false);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleCloseAttempt}
      // sx={{ pointerEvents: "none", zIndex: 1200 }}
      hideBackdrop={true}
      sx={{ pointerEvents: "none" }}
      ModalProps={{
        keepMounted: true,
        // No cubrir la barra superior: safe-area + 56px (misma cifra que restas en height)
        sx: {
          zIndex: 1500,
          top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          width: {
            xs: "100%",
            sm: "720px",
            md: "720px",
            lg: "720px",
          },
          margin: {
            xs: "0",
            sm: "0 auto",
            md: "0 auto",
            lg: "0 auto",
          },
          // height: "calc(100svh - env(safe-area-inset-top) - 56px)",
          height: isIOS || isAndroid
            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
            : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"), border: `0.5px solid ${PureLightThemeCaaS.colors.alpha.white[70]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          bgcolor: "#EDEDED",
        },
      }}
    >
      <Fade in={open}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            bgcolor: "#EDEDED",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <DialogTitle sx={{ bgcolor: "#EDEDED", borderTopLeftRadius: 5, borderTopRightRadius: 5, pb: 2, p: 1 }}>
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
              }}
            >
              <Box sx={{ visibility: "hidden" }}>
                {" "}
                <IconButton aria-label="close" onClick={handleCloseAttempt}>
                  <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
                </IconButton>
              </Box>
              {/* <IconButton aria-label="close" onClick={handleCloseAttempt}>
                <ArrowBackIcon sx={{ color: `${PureLightThemeCaaS.colors.alpha.black[100]}` }} />
              </IconButton> */}
              <Typography variant="h4" color={PureLightThemeCaaS.colors.alpha.black[100]}>
                AI Settings
              </Typography>
              <IconButton aria-label="close" onClick={handleCloseAttempt}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          {/* Divider for separation between the title and the cards */}
          <Divider
            sx={{
              margin: {
                xs: "auto",
                md: "0px",
              },
              width: "100%",
              bgcolor: "#000000",
              my: 0.5,
            }}
          />

          <Box sx={{ p: 1, pt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>

            <Typography
              variant="h5"
              color={PureLightThemeCaaS.colors.alpha.black[100]}
              sx={{ fontSize: "18px", fontWeight: "medium", pb: 1, textAlign: "center", lineHeight: 1.2 }}
            >
              For a faster experience, {isMobile ? <br /> : " "} these features may be disabled
            </Typography>
            {/* <Typography
              variant="h5"
              color={PureLightThemeCaaS.colors.alpha.black[100]}
              sx={{ fontSize: "18px", fontWeight: "medium", pb: 1, textAlign: "center",lineHeight: 1.2 }}
            >
              these features may be disabled
            </Typography> */}
            {aiSettingsMockOptions.map(({ id, title, description }) => (
              <Box
                key={id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  bgcolor: "#EDEDED",
                  borderRadius: 1,
                  border: "0.5px solid #000000",
                  boxShadow: "2px 2px 4px 1px rgba(0, 0, 0, 0.25)",
                  width: isMobile ? "100%" : "60%",
                  margin: "0 auto",
                }}
              >
                <Box px={1} py={0.5}>
                  <Typography variant="h5" color="#000000">
                    {title}
                  </Typography>
                  <Typography variant="body2" color="rgba(19, 19, 19, 0.7)">
                    {description}
                  </Typography>
                </Box>
                <Box pr={1} display="flex" alignItems="center">
                  <AiSwitchButton checked={localSettings[id] ?? false} onToggle={(value) => handleToggleLocalSetting(id, value)} />
                </Box>
              </Box>
            ))}

            <Box textAlign="center" pt={4}>
              <Button
                variant="contained"
                size="small"
                type="submit"
                disabled={isLoading || !hasChanges}
                sx={{
                  bgcolor: hasChanges ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.alpha.gray[30],
                  color: hasChanges ? undefined : PureLightThemeCaaS.colors.alpha.black[50],
                  width: "280px",
                  height: "48px",
                  fontSize: "1rem",
                  "&.Mui-disabled": {
                    bgcolor: PureLightThemeCaaS.colors.alpha.gray[30],
                    color: PureLightThemeCaaS.colors.alpha.black[50],
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Save"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Fade>
      <Dialog
        open={showUnsavedPrompt}
        onClose={handleKeepEditing}
        maxWidth="xs"
        fullWidth
        // make this dialog with z index higher than 1500 please
        sx={{
          zIndex: 1600,
        }}
        // slotProps={{
        //   Backdrop: {
        //     sx: {
        //       zIndex: 1501,
        //     },
        //   },
        // }}
        PaperProps={{
          sx: {
            bgcolor: "#EDEDED",
            borderRadius: 1,
            border: "0.5px solid #000000",
            boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
            p: 1.5,
           
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              // zIndex: 1600,
              position: "fixed",
              backdropFilter: "blur(1px) !important",
              WebkitBackdropFilter: "blur(1px) !important",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
            },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", color: PureLightThemeCaaS.colors.alpha.black[100], fontWeight: 700, p: 1 }}>Unsaved changes</DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 1 }}>
          <DialogContentText sx={{ color: "rgba(19,19,19,0.7)" }}>Your changes haven't been saved</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1 }}>
          <Button
            variant="contained"
            onClick={handleKeepEditing}
            autoFocus
            sx={{ flex: 1, whiteSpace: "nowrap", p: 0.5, bgcolor: PureLightThemeCaaS.colors.alpha.orange[100], "&:hover": { bgcolor: PureLightThemeCaaS.colors.alpha.orange[90] } }}
          >
            Keep editing
          </Button>
          <Button
            variant="outlined"
            onClick={handleDiscardChanges}
            sx={{
              flex: 1,
              whiteSpace: "nowrap",
              p: 0.5,
              borderColor: PureLightThemeCaaS.colors.alpha.black[70],
              color: PureLightThemeCaaS.colors.alpha.black[100],
              "&:hover": { borderColor: PureLightThemeCaaS.colors.alpha.black[100] },
            }}
          >
            Yes, exit
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};
